import { useEffect, useRef, useState } from 'react';
import { observeWithPool } from './observerPool';
import { scheduleRelease, scheduleResolve, type ScheduledResolve } from './resolveQueue';
import { bump } from './metrics';
import type { PhotoItem, PhotoSize } from '../photo-sources/types';

/**
 * Resolve a photo's URL only once its tile is near the viewport, and release it when the tile
 * drifts far away.
 *
 * There is deliberately **no `loading` state**. A tile renders identically whether it is idle or
 * fetching — the placeholder colour is already there — so a `loading` transition would re-render
 * hundreds of components during a fling and paint nothing. Only transitions that change output
 * are allowed to touch React state.
 */

export type PhotoUrlState =
  | { status: 'idle' }
  | { status: 'ready'; url: string }
  | { status: 'error'; error: Error };

/**
 * Start fetching this far outside the viewport. Generous on purpose: photos must be ready
 * *before* they are scrolled to, and the windowed layout only mounts a tile shortly before
 * this margin would fire anyway.
 */
const PRELOAD_MARGIN = '1000px';
/**
 * Release beyond this. The gap from PRELOAD_MARGIN is hysteresis: too narrow and tiles thrash
 * across the boundary.
 *
 * Measured at 2000px: 595 releases against 133 successful resolves — every released photo was
 * re-fetched over the network more than four times. Widened to 3000px. Memory stays bounded by
 * the source's LRU regardless, so retaining longer costs little.
 */
const RETAIN_MARGIN = '3000px';

export function usePhotoUrl(item: PhotoItem, size: PhotoSize) {
  const ref = useRef<HTMLElement | null>(null);
  // Seed from cache so a tile the windowing just remounted paints straight away rather than
  // blanking and fading back in.
  const [state, setState] = useState<PhotoUrlState>(() => {
    const cached = item.peek(size);
    return cached ? { status: 'ready', url: cached } : { status: 'idle' };
  });

  // Keep the latest size without making it an effect dependency — a resize shouldn't tear down
  // and rebuild every subscription in the grid.
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let cancelled = false;
    let scheduled: ScheduledResolve<string> | null = null;
    let cancelPendingRelease: (() => void) | null = null;
    let holdsUrl = false;

    /** Returning the previous object makes React bail out instead of re-rendering. */
    const goIdle = () =>
      setState((prev) => (prev.status === 'idle' ? prev : { status: 'idle' }));

    const unobservePreload = observeWithPool(element, PRELOAD_MARGIN, (entry) => {
      bump('ioPreload');
      if (!entry.isIntersecting) return;

      // Coming back into range cancels a release that hasn't run yet.
      cancelPendingRelease?.();
      cancelPendingRelease = null;

      if (scheduled) return;

      bump('resolveStarted');
      scheduled = scheduleResolve(() => item.resolve(sizeRef.current));
      scheduled.promise
        .then((url) => {
          bump('resolveDone');
          holdsUrl = true;
          if (!cancelled) setState({ status: 'ready', url });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          scheduled = null;
          setState({
            status: 'error',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        });
    });

    const unobserveRetain = observeWithPool(element, RETAIN_MARGIN, (entry) => {
      bump('ioRetain');
      if (entry.isIntersecting) return;

      // Nothing to give back. Most tiles are in this state — round 5 measured 520 releases
      // against 133 resolves, i.e. the majority were releasing nothing and re-rendering a tile
      // that was already idle.
      if (!scheduled && !holdsUrl) return;

      scheduled?.cancel();
      scheduled = null;

      // Deferred: tearing down an <img> mid-fling is DOM mutation and paint work for a tile
      // nobody is looking at. Runs as soon as the page settles.
      cancelPendingRelease = scheduleRelease(() => {
        bump('released');
        holdsUrl = false;
        item.release();
        if (!cancelled) goIdle();
      });
    });

    return () => {
      cancelled = true;
      scheduled?.cancel();
      cancelPendingRelease?.();
      unobservePreload();
      unobserveRetain();
      // Deliberately no `item.release()`. Windowing unmounts tiles constantly, and revoking
      // here meant every spread that scrolled out had its photos re-fetched on the way back —
      // the "already-loaded photos flash" symptom. Memory stays bounded by the source's LRU,
      // which is what that cache is for.
    };
  }, [item]);

  return { ref, state };
}
